import { usePostHog } from '@posthog/react';
import useAuth from 'hooks/useAuth';
import { useUiDesign } from 'hooks/useUiDesign';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { setUser } from 'utils/monitoring';
import { registerDeploymentProperties } from 'utils/posthog';

/**
 * Component that automatically manages monitoring user context based on AuthContext.
 * Must be placed inside AuthProvider to have access to auth state.
 */
const MonitoringUserProvider = ({ children }: PropsWithChildren): JSX.Element => {
    const { user } = useAuth();
    const { design, isImpersonating, isLoading: isDesignLoading } = useUiDesign();
    const posthog = usePostHog();
    const previousDistinctId = useRef<string | null>(null);
    const previousIdentity = useRef<string | null>(null);

    // TODO(react-doctor: no-effect-event-handler): monitoring and PostHog identity
    // synchronize with the async auth state, including restored sessions.
    useEffect(() => {
        if (user) {
            const userId = String(user.id);
            const tenant = user.tenants[0];
            const tenantName = user.tenantName || undefined;
            // Employee ids restart at 1 in every tenant, so the PostHog id carries the tenant.
            const distinctId = `${tenant}:${userId}`;

            if (previousDistinctId.current && previousDistinctId.current !== distinctId && posthog.__loaded) {
                posthog.reset();
                registerDeploymentProperties();
            }

            const identityProperties = {
                email: user.workEmailAddress || undefined,
                name: user.name,
                tenant,
                tenant_name: tenantName,
                position: user.jobPositionName || undefined,
                team: user.teamName || undefined,
                screen_width: window.screen.width,
                screen_height: window.screen.height,
            };
            const identity = `${distinctId}:${JSON.stringify(identityProperties)}`;
            if (tenant && posthog.__loaded) {
                if (previousIdentity.current !== identity) {
                    posthog.identify(distinctId, identityProperties);
                    posthog.register({ tenant, tenant_name: tenantName });
                    previousIdentity.current = identity;
                }
                if (user.authUserId && user.authUserId !== user.id) {
                    posthog.register({ is_impersonated_session: true, actor_id: `${tenant}:${user.authUserId}` });
                } else {
                    posthog.unregister('is_impersonated_session');
                    posthog.unregister('actor_id');
                }
            }
            setUser({
                email: user.workEmailAddress || undefined,
                id: userId,
            });
            previousDistinctId.current = distinctId;
        } else {
            if (previousDistinctId.current && posthog.__loaded) {
                posthog.reset();
                registerDeploymentProperties();
            }
            setUser(null);
            previousDistinctId.current = null;
            previousIdentity.current = null;
        }
    }, [posthog, user]);

    // Declared after the identity effect: a change of user resets PostHog there, and this registers the
    // design again, so every event carries it.
    useEffect(() => {
        if (!user || isDesignLoading || !posthog.__loaded) return;
        const uiDesign = design.toLowerCase();
        posthog.register({ ui_design: uiDesign });
        // The person identified is the impersonated user, and the design of a session acting as them is not theirs.
        if (!isImpersonating) posthog.setPersonProperties({ ui_design: uiDesign });
    }, [posthog, user, design, isImpersonating, isDesignLoading]);

    return <>{children}</>;
};

export default MonitoringUserProvider;
